import React, { useEffect, useRef, useState } from 'react';
import './player-checkpoint-page.styles.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { format } from 'date-fns';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import Modal, { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import PlayerSubmitForm from '@/components/PLAYER/challenges/player-submit-form.component';
import TransactionItemCard from '@/components/SHARED/transaction-view/transaction-item-card.component';
import UploadedFileButton from '@/components/SHARED/uploaded-file-button/uploaded-file-button.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCheck, faHourglassHalf, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { UseCloudGameTransactions } from '@/utils/firebase.utils';
import { getActionItemsFromFirebase } from '@/utils/getDataFromDb';
import { RandomString } from '@/utils/randomString';
import linkifyHtml from 'linkify-html';
import useAnalytics from '@/utils/useAnalytics';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { checkElementAccess } from '@/utils/checkElementAccess';

const PlayerCheckpointPage = ({
  questData,
  gameData,
  gameElements,
  playerData,
  questProgress,
  playerProgress,
  gameStats,
  teamMembers
}) => {

  const { checkpointPath } = useParams();
  const navigate = useNavigate();
  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const storage = getStorage();
  const { trackEvent, trackException } = useAnalytics();

  const checkpoints = questData?.checkpoints || [];
  const checkpointIndex = checkpoints.findIndex(cp => cp.path === checkpointPath);
  const checkpoint = checkpoints[checkpointIndex] || null;
  const allowMultipleTries = checkpoint?.opt_multipleTries !== false;

  const isAccessible = checkpointIndex === 0 || (
    checkpointIndex > 0 &&
    questProgress?.checkpoints?.[checkpoints[checkpointIndex - 1]?.id]?.status === 'approved'
  );

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [completionRecords, setCompletionRecords] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [pending, setPending] = useState(false);
  const [defaultRewardsList, setDefaultRewardsList] = useState([]);
  const [settingsList, setSettingsList] = useState([]);
  const [viewSubmissionData, setViewSubmissionData] = useState(null);
  const dataRef = useRef();
  const completionRef = useRef({});
  const approveRef = useRef();
  let completionListener;

  useEffect(() => {
    return () => {
      if (completionListener) completionListener();
    }
  }, [])

  useEffect(() => {
    if (!questData) return;
    const access = checkElementAccess({
      playerData,
      gameElements,
      elementId: questData.id,
      gameStats,
      teamMembers,
      type: 'quest'
    })
    if (!access.access) {
      setAlertMessage(`You do not have access to this Quest. Reason: ${access.reason}`);
      approveRef.current = () => navigate(-1);
      return;
    }
  }, [questData])

  useEffect(() => {
    if (!checkpoint || !playerData) return;
    if (checkpoint.opt_displayRewards) {
      getRewards();
    }
    getSettings();
    getCompletionRecords();
  }, [checkpoint, playerData])

  function getRewards() {
    if (
      playerData?.checkpoints?.[checkpoint.id]?.approved
    ) return;

    let rewardArr = [];
    for (let key of Object.keys(checkpoint)) {
      if (!key.includes('earned')) continue;
      if (key === 'opt_earnedPoints') {
        rewardArr.push({
          'id': RandomString(10),
          'icon': '/images/xp.png',
          'quantity': checkpoint[key],
          'title': `${checkpoint[key]} Points Earned`,
          'type': 'points'
        })
      }
      if (key === 'opt_earnedCurrency') {
        rewardArr.push({
          'id': RandomString(10),
          'icon': '/images/icons/coin.png',
          'quantity': checkpoint[key],
          'title': `$${checkpoint[key]} Earned`,
          'type': 'currency'
        })
      }
      if (key === 'opt_earnedItems' || key === 'opt_earnedPrizePacks') {
        for (let earnedEl of Object.values(checkpoint[key])) {
          const element = gameElements?.[earnedEl.id];
          if (!element) continue;
          rewardArr.push({
            'id': RandomString(10),
            'icon': element.icon,
            'quantity': earnedEl.quantity,
            'title': `${element.type === 'prizepack' ? 'Prize Pack' : 'Item'} Earned: ${element.name}`,
            'elementId': earnedEl.id,
            'type': element.type
          })
        }
      }
    }
    setDefaultRewardsList([...rewardArr]);
  }

  function getSettings() {
    let infoArr = [];
    if (checkpoint.opt_multiple) {
      infoArr.push({
        'id': RandomString(10),
        'text': 'You can complete this Checkpoint multiple times.',
        'class': null
      })
    }
    setSettingsList([...infoArr]);
  }

  function getCompletionRecords() {
    completionRef.current = {};
    getActionItemsFromFirebase({
      'gameId': gameData.gameId,
      'playerId': playerData.playerId,
      'elementId': checkpoint.id,
      'callback': callback,
      'handleListener': handleListener
    })

    function callback(data) {
      completionRef.current = { ...completionRef.current, ...{ [data.id]: data } }
      setCompletionRecords(Object.values(completionRef.current));
      getCompletionStatus();
    }

    function getCompletionStatus() {
      for (let r of Object.values(completionRef.current).sort((a,b) => a.ts > b.ts ? 1 : -1)) {
        console.log(r);
        if (
          !allowMultipleTries ||
          (
            !checkpoint.opt_multiple &&
            (r.status === 'approved' || r.status === 'pending')
          )
        ) {
          setCompleted(true);
        } 
        if (r.status === 'pending') {
          setPending(true);
        }
        if (r.status === 'declined') {
          setCompleted(false);
          setPending(false);
        }
      }
    }

    function handleListener(unsub) {
      completionListener = unsub;
    }
  }

  function saveData(key, value) {
    dataRef.current = { ...dataRef.current, ...{ [key]: value } }
    setFormData(dataRef.current);
  }

  async function handleSubmit() {
    const tempData = { ...formData };
    if (!tempData.inputText) {
      setAlertMessage('Be sure to include something in the text box before submitting.');
      return;
    }
    setConfirmMessage('Are you sure you are ready to submit this Checkpoint response?');
    approveRef.current = () => continueApproval();

    async function continueApproval() {
      approveRef.current = null;
      setLoading(true);
      const now = new Date().getTime();
      const gameCode = gameData.gameId.substring(
        gameData.gameId.indexOf('-') - 5,
        gameData.gameId.indexOf('-') + 6
      );
      tempData.gameId = gameData.gameId;
      tempData.event = 'Checkpoint Submitted';
      tempData.playerId = playerData.playerId;
      tempData.type = 'checkpoint';
      tempData.elementId = checkpoint.id;
      tempData.questId = questData.id;
      tempData.checkpointPath = checkpoint.path;
      tempData.id = `${now}-${gameCode}-checkpoint-actionitem`;
      tempData.status = 'pending';
      tempData.ts = now;
      tempData.ts_submitted = now;
      tempData.autoApproval = checkpoint.opt_autoApprove === true;
      tempData.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz-_0123456789');
      if (tempData.upload) {
        const filePath = await uploadFile(tempData.file, now);
        tempData.filePath = filePath;
        delete tempData.upload;
        delete tempData.file;
      }
      const res = await UseCloudGameTransactions(
        'submitActionItem',
        {
          'playerId': tempData.playerId,
          'gameId': gameData.gameId,
          'actionItemData': tempData,
          'access': 'quests'
        }
      )
      if (res.error) {
        setAlertMessage(`Something went wrong. Please try again later. (Error: ${res.error})`);
        setLoading(false);
        trackException('Player Checkpoint Submission Error: ' + res.error);
        return;
      } else if (res.denied) {
        setAlertMessage(`Sorry, your access was denied. Reason: ${res.denied}`);
        setLoading(false);
        trackException('Player Checkpoint Submission Denied: ' + res.denied);
        return;
      }
      setLoading(false);
      trackEvent(
        'checkpoint_submitted',
        'Player',
        `Player ${userProfile.appId} submitted checkpoint: ${checkpoint.id}`,
        null,
        null
      );
      dataRef.current = {};
      setFormData({});
      if (!res.autoApproval) {
        setAlertMessage('Your submission was successful! It is now pending review by the game manager. You will be notified when it has been reviewed.');
    //   } else {
    //     getCompletionRecords();
      }
    }
  }

  async function uploadFile(file, now) {
    const filePath = `${gameData.gameId}/${checkpoint.id}/${now}-${file.name}`;
    const uploadRef = ref(storage, filePath);
    const metadata = {
      contentType: file.type,
      customMetadata: { appId: userProfile.appId }
    };
    await uploadBytes(uploadRef, file, metadata);
    return filePath;
  }

  function renderDesc() {
    if (!checkpoint.desc) return null;
    const html = Array.isArray(checkpoint.desc)
      ? checkpoint.desc.join('<br />')
      : checkpoint.desc;
    return (
      <div
        className='checkpoint-desc'
        dangerouslySetInnerHTML={{ __html: linkifyHtml(html) }}>
      </div>
    );
  }

  function isCheckpointComplete(cp) {
    return questProgress?.checkpoints?.[cp.id]?.status === 'approved';
  }

  function isCheckpointAccessible(index) {
    if (index === 0) return true;
    return isCheckpointComplete(checkpoints[index - 1]);
  }

  if (!checkpoint) {
    return (
      <div className='g-card'>
        <BackButton cancel={() => navigate(-1)} />
        <p className='meta'>Checkpoint not found.</p>
      </div>
    );
  }

  if (!isAccessible) {
    return (
      <div className='player-checkpoint-page'>
        <div className='player-checkpoint-card g-card'>
          <BackButton cancel={() => navigate(-1)} />
          <p className='meta'>You must complete the previous checkpoint before accessing this one.</p>
        </div>
      </div>
    );
  }

  const mapDisplay = questData?.mapImageUrl ? (
    <div className='cp-map-display'>
      <div className='qmap-image-wrapper'>
        <img
          src={
            questData.mapImageUrl.includes('http')
              ? questData.mapImageUrl
              : `https://app.gamably.com${questData.mapImageUrl}`
          }
          alt='Quest Map'
          className='qmap-image'
          draggable={false}
        />
        {checkpoints.length > 1 && (
          <svg
            className='qmap-svg-overlay'
            viewBox='0 0 100 100'
            preserveAspectRatio='none'>
            <defs>
              <filter id='qmap-line-shadow-cp' x='-20%' y='-20%' width='140%' height='140%'>
                <feDropShadow dx='0' dy='2' stdDeviation='1.5' floodColor='rgba(0,0,0,0.4)' />
              </filter>
            </defs>
            {checkpoints.slice(1).map((pt, i) => (
              <line
                key={i}
                x1={checkpoints[i].xPct * 100}
                y1={checkpoints[i].yPct * 100}
                x2={pt.xPct * 100}
                y2={pt.yPct * 100}
                className='qmap-line'
                filter='url(#qmap-line-shadow-cp)'
              />
            ))}
          </svg>
        )}
        {checkpoints.map((pt, i) => {
          const complete = isCheckpointComplete(pt);
          const accessible = isCheckpointAccessible(i);
          const isCurrent = i === checkpointIndex;
          return (
            <div
              key={pt.id}
              className={`qmap-pin ${isCurrent
                ? 'qmap-pin--current'
                : complete
                  ? 'qmap-pin--complete'
                  : accessible
                    ? 'qmap-pin--available'
                    : 'qmap-pin--locked'}`}
              style={{ left: `${pt.xPct * 100}%`, top: `${pt.yPct * 100}%` }}
            >
              <span className='qmap-pin-label'>{i + 1}</span>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div className='player-checkpoint-page'>
      <div className='player-checkpoint-card g-card'>
        <BackButton cancel={() => navigate(-1)} />
        <div className='g-space-1'></div>
        <div className='meta breadcrumb'>
          {questData.name} &rsaquo; Checkpoint {checkpointIndex + 1}
        </div>
        <div className="g-space-0-5"></div>
        <div className={`checkpoint-layout ${screenSize}${!mapDisplay ? ' no-map' : ''}`}>
          <div className='checkpoint-left'>
            <h3 className='checkpoint-title'>
              <ImageContainer
                src={checkpoint?.icon || '/images/icons/checkpoint.png'}
                alt='checkpoint icon'
                className='checkpoint-title-icon'
              />
              {checkpoint?.name}
            </h3>
            {(screenSize === 'mobile' || screenSize === 'tablet') && mapDisplay}
            
          </div>
          {mapDisplay && (
            <div className='checkpoint-right'>
              {(screenSize !== 'mobile' && screenSize !== 'tablet') && mapDisplay}
            </div>
          )}
            <div>
              {renderDesc()}
            </div>
          
        </div>
        <div className='g-space-1'></div>
        {
          (defaultRewardsList.length > 0) &&
          <div className='reward-display'>
            <hr />
            <h3>Default Rewards</h3>
            <div className='reward-list'>
              {
                defaultRewardsList.map((r, i) => (
                  <div key={r.id + i}>
                    <TransactionItemCard elementData={r} small={true} />
                  </div>
                ))
              }
            </div>
          </div>
        }
        {
          (settingsList.length > 0) &&
          <div className='settings-display'>
            <hr />
            <h3>Checkpoint Settings</h3>
            <ul className='settings-list'>
              {settingsList.map(r => (
                <li key={r.id} className={r.class}>{r.text}</li>
              ))}
            </ul>
          </div>
        }
        {
          (completionRecords.length > 0) &&
          <div className='completion-records'>
            <hr />
            <h3>Previous Submissions</h3>
            <div className='record-list'>
              {
                (screenSize !== 'mobile' && screenSize !== 'tablet') &&
                <div className='record records-header'>
                  <div></div>
                  <div>Status</div>
                  <div>Submitted</div>
                  <div>Evaluated</div>
                  <div>Comments</div>
                  <div></div>
                </div>
              }
              {
                completionRecords
                  .sort((a, b) => a.ts < b.ts ? 1 : -1)
                  .map(a => {
                    const icon = a.status === 'approved' ? faCheck
                      : a.status === 'declined' ? faBan
                      : faHourglassHalf;
                    if (screenSize !== 'mobile' && screenSize !== 'tablet') {
                      return (
                        <div key={a.id} className={`record g-list-item ${screenSize}`}>
                          <div>
                            <FontAwesomeIcon icon={icon} className={a.status} size='xl' />
                          </div>
                          <div className='outcome'>{a.status.toUpperCase()}</div>
                          <div className='date'>{format(new Date(a.ts_submitted), 'Pp')}</div>
                          <div className='evaluated'>
                            {a.approval_ts ? format(new Date(a.approval_ts), 'Pp') : ''}
                          </div>
                          <div className='comments'>
                            {a.comments
                              ? <span>"{a.comments}"<br />- {a.approval_name}</span>
                              : ''}
                          </div>
                          <div>
                            <button
                              className='g-button small-btn'
                              onClick={() => setViewSubmissionData(a)}>
                              <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </button>
                          </div>
                        </div>
                      )
                    } else {
                      return (
                        <div key={a.id} className='mobile-record g-list-item'>
                          <div className='top-row'>
                            <FontAwesomeIcon icon={icon} className={a.status} size='xl' />
                            {a.status.toUpperCase()}
                            <div>
                              <button
                                className='g-button small-btn'
                                onClick={() => setViewSubmissionData(a)}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                              </button>
                            </div>
                          </div>
                          <div className='meta'>
                            Submitted: {format(new Date(a.ts_submitted), 'Pp')}
                          </div>
                          {a.approval_ts &&
                            <div className='meta'>
                              Evaluated: {format(new Date(a.approval_ts), 'Pp')}
                            </div>
                          }
                          {a.comments &&
                            <div className='meta'>Comments: "{a.comments}"</div>
                          }
                        </div>
                      )
                    }
                  })
              }
            </div>
          </div>
        }
        <hr />
        {
          (!completed && !pending) &&
          <>
            <div className='g-space-1'></div>
            <h3>Submit a Response</h3>
            <div className='submit-div'>
              <PlayerSubmitForm
                elementData={checkpoint}
                formData={formData}
                saveData={saveData}
                type='checkpoint' />
            </div>
            <div className='g-space-1'></div>
            <hr />
            {
              loading
                ? <div className='buttons'>
                    <button className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                      Cancel
                    </button>
                    <button className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                      <ThreeDotsLoader />
                    </button>
                  </div>
                : <div className='buttons'>
                    <button
                      className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                      onClick={() => navigate(-1)}>
                      Cancel
                    </button>
                    <button
                      className={`g-button primary ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                      onClick={() => handleSubmit()}>
                      Submit Your Response
                    </button>
                  </div>
            }
          </>
        }
      </div>
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={approveRef.current} />
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <Modal
        show={!!viewSubmissionData}
        cancel={() => setViewSubmissionData(null)}
        closeButton={true}
        text='View what you submitted'>
        {
          viewSubmissionData &&
          <div className='modal-children'>
            <div dangerouslySetInnerHTML={{
              __html: linkifyHtml(viewSubmissionData.inputText, { target: '_blank' })
            }}></div>
            <div className='g-space-1'></div>
            {viewSubmissionData.filePath &&
              <UploadedFileButton
                path={viewSubmissionData.filePath}
                name={viewSubmissionData.fileName}
                type={viewSubmissionData.fileType} />
            }
            {(!viewSubmissionData.filePath && viewSubmissionData.fileName) &&
              <div className='meta'>Submitted File: {viewSubmissionData.fileName}</div>
            }
          </div>
        }
        <div className='g-space-1'></div>
        <div className='buttons'>
          <button className='g-button' onClick={() => setViewSubmissionData(null)}>Ok</button>
        </div>
      </Modal>
      <FullPageLoader show={loading} />
    </div>
  );
};

export default PlayerCheckpointPage;
